import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from 'react';
import { useClassGradeSubjectStructureHook } from '../hooks/useClassGradeSubjectStructureHook';
import {
  IClassGradeSubjectStructureResponse,
  IClassGradeSubjectStructure,
} from '../../../../apis/institute/institute.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { UseQueryResult } from '@tanstack/react-query';

type TQueryError = IFHError<{ message: string }>;

interface TClassGradeSubjectStructureState {
  classGradeSubjectStructureQuery: UseQueryResult<
    IClassGradeSubjectStructureResponse,
    TQueryError
  >;
  getSubjectsByClassId: (classId: number) => IClassGradeSubjectStructure | undefined;
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

interface ClassGradeSubjectStructureProviderProps {
  children: ReactNode;
}

const ClassGradeSubjectStructureContext = createContext<
  TClassGradeSubjectStructureState | undefined
>(undefined);

export const ClassGradeSubjectStructureProvider: React.FC<
  ClassGradeSubjectStructureProviderProps
> = ({ children }) => {
  const classGradeSubjectStructureQuery = useClassGradeSubjectStructureHook();

  const getSubjectsByClassId = useMemo(
    () => (classId: number): IClassGradeSubjectStructure | undefined => {
      return classGradeSubjectStructureQuery.data?.classes.find(
        (cls) => cls.classId === classId
      );
    },
    [classGradeSubjectStructureQuery.data]
  );

  const isLoading = classGradeSubjectStructureQuery.isLoading;
  const isError = classGradeSubjectStructureQuery.isError;
  const error = classGradeSubjectStructureQuery.error || null;

  const value = useMemo(
    () => ({
      classGradeSubjectStructureQuery,
      getSubjectsByClassId,
      isLoading,
      isError,
      error,
    }),
    [
      classGradeSubjectStructureQuery,
      getSubjectsByClassId,
      isLoading,
      isError,
      error,
    ]
  );

  return (
    <ClassGradeSubjectStructureContext.Provider value={value}>
      {children}
    </ClassGradeSubjectStructureContext.Provider>
  );
};

export const useClassGradeSubjectStructureContext =
  (): TClassGradeSubjectStructureState => {
    const context = useContext(ClassGradeSubjectStructureContext);

    if (context === undefined) {
      throw new Error(
        'useClassGradeSubjectStructureContext must be used within a ClassGradeSubjectStructureProvider'
      );
    }

    return context;
  };
